interface HttpError {
  status?: number
  code?: string
  message?: string
}

interface RetryOptions {
  maxRetries?: number
  baseDelay?: number
  maxDelay?: number
  retryCondition?: (error: HttpError) => boolean
}

export class RetryableError extends Error {
  shouldRetry: boolean
  retryAfter?: number

  constructor(message: string, shouldRetry: boolean = true, retryAfter?: number) {
    super(message)
    this.name = 'RetryableError'
    this.shouldRetry = shouldRetry
    this.retryAfter = retryAfter
  }
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 16000,
    retryCondition = (error) => {
      if (error instanceof RetryableError) {
        return error.shouldRetry
      }
      // Default retry condition for common transient errors
      if (error?.status === 429) return true // Rate limited
      if (error?.status === 500) return true // Internal server error
      if (error?.status === 502) return true // Bad gateway
      if (error?.status === 503) return true // Service unavailable
      if (error?.status === 504) return true // Gateway timeout
      if (error?.code === 'ECONNRESET') return true // Connection reset
      if (error?.code === 'ETIMEDOUT') return true // Timeout
      return false
    }
  } = options

  let lastError: Error | undefined

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      // Don't retry if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        throw error
      }

      lastError = error instanceof Error ? error : new Error(String(error))

      // If this is the last attempt or error is not retryable, throw
      if (attempt === maxRetries || !retryCondition(error as HttpError)) {
        throw error
      }

      // Calculate delay with exponential backoff and jitter
      let delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay)

      // Handle Retry-After header from rate limiting
      if (error instanceof RetryableError && error.retryAfter) {
        delay = Math.max(delay, error.retryAfter * 1000)
      }

      // Add jitter (random factor between 0.5 and 1.5) to prevent thundering herd
      const jitter = 0.5 + Math.random()
      delay = Math.floor(delay * jitter)

      const errorMessage = error instanceof Error ? error.message : String(error)
      console.warn(`Attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delay}ms:`, errorMessage)

      await sleep(delay)
    }
  }

  // This should never be reached, but TypeScript requires it
  throw lastError || new Error('Retry failed with unknown error')
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function isRetryableHttpError(response: Response): boolean {
  return (
    response.status === 429 || // Rate limited
    response.status === 500 || // Internal server error
    response.status === 502 || // Bad gateway
    response.status === 503 || // Service unavailable
    response.status === 504    // Gateway timeout
  )
}

export function createRetryableErrorFromResponse(response: Response, message?: string): RetryableError {
  const shouldRetry = isRetryableHttpError(response)
  const retryAfter = response.headers.get('Retry-After')

  return new RetryableError(
    message || `HTTP ${response.status}: ${response.statusText}`,
    shouldRetry,
    retryAfter ? parseInt(retryAfter, 10) : undefined
  )
}